import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header/Header';
import getMenuItems from '@const/menuItems';
import MenuSidebar from '../../components/MenuSidebar/MenuSidebar';
import { getUserInfo, getAllOrders, syncOfflineData } from '../../services/orderService';
import { useTierIcon } from '../../hooks/useTierIcon';
import loyaltyService, { type FormattedPointHistoryItem } from '../../services/loyaltyService';
import { ScrollArea, ScrollBar } from '@/ui/scroll-area';
import { Modal, ModalHeader, ModalContent, ModalTitle, ModalCloseButton } from '@/ui/modal';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '@hooks/useAuth';
import { logger } from '../../utils/logger';
import { PrivacySettings } from '../../components/PrivacySettings/PrivacySettings';
import { useAuthDialog } from '../../contexts/AuthDialogContext';
import { useLanguageChange } from '@hooks/languageChange';
import { useNotification } from '@hooks/useNotification';
import { getFullVersionInfo } from '../../utils/version';
import {
  VerifiedIcon,
  CheckCircle,
  History,
  Calendar,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Phone,
} from 'lucide-react';

import { Button } from '@/ui/button';
import getUserInitials from '@hooks/getUserInitials';
import getAvatarColor from '@hooks/getAvatarColor';
import { Avatar, type LoyaltyTier } from '@/ui/avatar';
import { useMembership } from '../../hooks/useMembership';

import { useSlideUpAnimation } from '@hooks/useSlideUpAnimation';
import { useProgressAnimation } from '@hooks/useProgressAnimation';
import bronzeIcon from '../../assets/loyalty/bronze.png';
import silverIcon from '../../assets/loyalty/silver.png';
import goldIcon from '../../assets/loyalty/gold.png';
import { isDesktop, isMobile } from 'react-device-detect';
import merchantService, { type MerchantRecord } from '../../services/merchantService';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  LegalDocument01FreeIcons,
  Timer01FreeIcons,
  FileVerifiedFreeIcons,
  CancelCircleFreeIcons,
  Store01FreeIcons,
} from '@hugeicons/core-free-icons';
import { DEFAULT_FALLBACK_IMAGE } from '../../utils/imageFallbackUtils';
import { useMonthlyDetailsDialog } from '../../contexts/MonthlyDetailsDialogContext';
import ReactECharts from 'echarts-for-react';
import 'echarts-wordcloud';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useOnlineStatus } from '@hooks/useOnlineStatus';

interface UserProfile {
  id: string;
  name: string;
  phoneNumber: string;
  email?: string;
  activeRole: string;
  isVerified?: boolean;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  createdAt?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const ResponsiveScrollArea = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  if (isMobile) {
    return <div className={className}>{children}</div>;
  }
  return (
    <ScrollArea className={className}>
      {children}
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
};

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { isAuthenticated, user } = useAuth();
  const { showAuthDialog } = useAuthDialog();
  const { changeLanguage } = useLanguageChange();
  const { permission, isSupported, requestPermission, showNotification } = useNotification();
  const {
    membershipInfo,
    isLoading: membershipLoading,
    getPointsToNextTier,
    getProgressPercentage,
  } = useMembership();
  const tierIconProps = useTierIcon();
  const navigate = useNavigate();

  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  useEffect(() => {
    const checkPWAInstalled = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
      const isMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;
      const isIOSStandalone =
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
      setIsPWAInstalled(standalone || isFullscreen || isMinimalUI || isIOSStandalone);
    };
    checkPWAInstalled();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkPWAInstalled);
    return () => mediaQuery.removeEventListener('change', checkPWAInstalled);
  }, []);

  // Slide-up animation for points
  const pointsAnimation = useSlideUpAnimation(membershipInfo?.pointsBalance || 0, {
    duration: 1500,
    delay: 600,
    staggerDelay: 10,
  });

  // Progress bar animation
  const progressAnimation = useProgressAnimation(getProgressPercentage(), {
    duration: 1500,
    delay: 600,
  });

  // Function to get progress bar color based on tier
  const getTierProgressColor = () => {
    const tier = tierIconProps.loyaltyTier;
    switch (tier) {
      case 'bronze':
        return 'bg-[linear-gradient(to_right,_#D3A575,_#B8956A)]';
      case 'silver':
        return 'bg-[linear-gradient(to_right,_#C6C1AF,_#B0AB9A)]';
      case 'gold':
        return 'bg-[linear-gradient(to_right,_#FFC200,_#E6AE00)]';
      default:
        return 'bg-[linear-gradient(to_right,_#d1d5db,_#9ca3af)]'; // default fallback
    }
  };

  // Tier benefits data
  const getTierBenefits = (tier: string) => {
    const benefits = {
      bronze: {
        en: ['1 point = 1 MMK', 'Earn 5 point per 1000 MMK spent'],
        mm: ['၁ မှတ် = ၁ ကျပ်', 'ငွေ ၁၀၀၀ ကျပ်သုံးတိုင်း ၅ မှတ်ရရှိ'],
      },
      silver: {
        en: ['1 point = 1 MMK', 'Earn 10 points per 1000 MMK spent'],
        mm: ['၁ မှတ် = ၁ ကျပ်', 'ငွေ ၁၀၀၀ ကျပ်သုံးတိုင်း ၁၀ မှတ်ရရှိ'],
      },
      gold: {
        en: ['1 point = 1 MMK', 'Earn 15 points per 1000 MMK spent'],
        mm: ['၁ မှတ် = ၁ ကျပ်', 'ငွေ ၁၀၀၀ ကျပ်သုံးတိုင်း ၁၅ မှတ်ရရှိ'],
      },
    };

    const currentLang = i18n.language;
    const tierKey = tier.toLowerCase() as keyof typeof benefits;
    return benefits[tierKey]?.[currentLang as 'en' | 'mm'] || [t('loyalty.not_available')];
  };

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);

  const [chatbotEnabled, setChatbotEnabled] = useState(() => {
    const saved = localStorage.getItem('chatbotEnabled');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationsEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [notificationSoundEnabled, setNotificationSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationSoundEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [saveRecentEnabled, setSaveRecentEnabled] = useState(
    localStorage.getItem('save_recent_phones') !== 'false',
  );
  const [offlineModeEnabled, setOfflineModeEnabled] = useState(
    localStorage.getItem('offline_mode_enabled') === 'true',
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);

  const handleSaveRecentToggle = (enable: boolean) => {
    try {
      localStorage.setItem('save_recent_phones', enable ? 'true' : 'false');
    } catch (e) {
      logger.warn('Failed to persist privacy setting to localStorage', e);
    }
    setSaveRecentEnabled(enable);
    showToast(
      'success',
      enable
        ? t('privacy.settings.recentPhones.toastEnabled', 'Save recent phone numbers turned on')
        : t('privacy.settings.recentPhones.toastDisabled', 'Save recent phone numbers turned off'),
    );
  };

  const handleOfflineModeToggle = async (enabled: boolean) => {
    setOfflineModeEnabled(enabled);
    localStorage.setItem('offline_mode_enabled', String(enabled));

    if (enabled && navigator.onLine) {
      setIsSyncing(true);
      setSyncProgress(0);
      try {
        await syncOfflineData((progress) => {
          setSyncProgress(progress);
        });
        showToast('success', 'Offline data synced successfully');
      } catch (error) {
        console.error('Sync failed:', error);
        showToast('error', 'Failed to sync offline data');
      } finally {
        setIsSyncing(false);
        setSyncProgress(0);
      }
    }
    showToast('success', enabled ? 'Offline mode enabled' : 'Offline mode disabled');
  };

  const handleClearLocalData = () => {
    localStorage.removeItem('offline_orders_list');
    localStorage.removeItem('offline_order_details');
    localStorage.removeItem('offline_user_profile');
    localStorage.removeItem('offline_scratched_cards');
    showToast('success', 'Local data cleared successfully');
  };

  // Point History Dialog State
  const [showPointHistory, setShowPointHistory] = useState(false);
  const [pointHistory, setPointHistory] = useState<FormattedPointHistoryItem[]>([]);
  const [pointHistoryLoading, setPointHistoryLoading] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [itemsPerPage] = useState(10);

  // Merchant Status State
  const [merchantStatus, setMerchantStatus] = useState<string | null>(null);
  const [merchantStatusLoading, setMerchantStatusLoading] = useState(false);
  const [merchantStatusReason, setMerchantStatusReason] = useState<string | null>(null);
  const [merchantStatusReviewedAt, setMerchantStatusReviewedAt] = useState<string | null>(null);
  // Merchant Record State
  const [merchantRecord, setMerchantRecord] = useState<MerchantRecord | null>(null);
  const [merchantRecordLoading, setMerchantRecordLoading] = useState(false);

  // Merchant computed display values
  const shopDisplayName = useMemo(() => {
    const name =
      merchantRecord?.shopName?.trim() ||
      merchantRecord?.businessName?.trim() ||
      userProfile?.name?.trim();
    return name || 'N/A';
  }, [merchantRecord, userProfile]);

  const coverImageUrl = useMemo(() => {
    return merchantRecord?.shopImageFile || DEFAULT_FALLBACK_IMAGE;
  }, [merchantRecord]);

  const addressLine = useMemo(() => {
    if (!merchantRecord) return '';
    return [merchantRecord.address, merchantRecord.city, merchantRecord.region]
      .filter((v) => !!v && String(v).trim().length > 0)
      .join(', ');
  }, [merchantRecord]);

  // Build merchant map link from environment variable (fallback to production URL)
  const getMerchantMapLink = (merchantId: string) => {
    const baseUrl =
      (import.meta.env.VITE_MERCHANT_MAP_BASE_URL as string) || 'https://location.myeztopup.com';
    return `${baseUrl}/locator/${merchantId}`;
  };

  // Point Summary State (unused variables removed)

  // Monthly analytics state (Account Settings)
  interface OrderItem {
    productId: string;
    name?: string | { my?: string; en?: string };
    quantity?: number;
    discountAmount?: number;
    discountType?: 'percentage' | 'fixed' | string;
    discountValue?: number;
    discountPrice?: number;
  }
  interface Order {
    items: OrderItem[];
    totalAmount: number;
    createdAt: string;
  }
  const [monthlyOrders, setMonthlyOrders] = useState<Order[]>([]);
  const [monthlyTotals, setMonthlyTotals] = useState({
    orderCount: 0,
    totalAmount: 0,
    pointsEarned: 0,
    commissionEarned: 0,
  });
  const [wordCloud, setWordCloud] = useState<Array<{ word: string; count: number }>>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const { openMonthlyDetails } = useMonthlyDetailsDialog();
  const initialDate = new Date();
  const [selectedYear] = useState<number>(initialDate.getFullYear());
  const [selectedMonth] = useState<number>(initialDate.getMonth());
  const [allOrders, setAllOrders] = useState<Order[]>([]);

  // Three-month aggregation state
  const [threeMonthAgg, setThreeMonthAgg] = useState<{
    labels: string[];
    revenue: number[];
    orders: number[];
    commission?: number[];
  }>({ labels: [], revenue: [], orders: [], commission: [] });
  const [threeMonthLoading, setThreeMonthLoading] = useState(false);

  // Radial word cloud layout state
  // const cloudRef = useRef<HTMLDivElement | null>(null);
  // const [cloudSize, setCloudSize] = useState({ w: 600, h: 360 });

  // useEffect(() => {
  //   const updateSize = () => {
  //     const el = cloudRef.current;
  //     if (el) setCloudSize({ w: el.clientWidth, h: el.clientHeight });
  //   };
  //   updateSize();
  //   window.addEventListener('resize', updateSize);
  //   return () => window.removeEventListener('resize', updateSize);
  // }, []);

  // const cloudLayout = useMemo(() => {
  //   if (!wordCloud || wordCloud.length === 0 || cloudSize.w === 0 || cloudSize.h === 0) return [] as any[];
  //   const maxCount = Math.max(...wordCloud.map((i) => i.count));
  //   const GOLDEN_ANGLE = 2.399963229728653; // radians
  //   const cx = cloudSize.w / 1.295;
  //   const cy = cloudSize.h / 2;
  //   return wordCloud.map((it, idx) => {
  //     const weight = maxCount ? it.count / maxCount : 0;
  //     const fontPx = Math.round(18 + weight * 26); // 18–44px
  //     const scale = 0.9 + weight * 0.5; // 0.9–1.4
  //     const angle = idx * GOLDEN_ANGLE;
  //     const radius = idx === 0 ? 0 : 28 + 24 * Math.sqrt(idx);
  //     const x = cx + radius * Math.cos(angle);
  //     const y = cy + radius * Math.sin(angle);
  //     const rotation = 0; // keep text horizontal
  //     const palette = idx % 2 === 0 ? 'from-sky-500 to-indigo-400' : 'from-orange-400 to-amber-500';
  //     const opacity = 0.35 + weight * 0.65;
  //     return { ...it, x, y, fontPx, scale, rotation, palette, opacity } as any;
  //   });
  // }, [wordCloud, cloudSize]);

  // Fetch all orders for the current month across pages to compute analytics
  useEffect(() => {
    const fetchMonthlyOrders = async () => {
      try {
        setMonthlyLoading(true);
        let page = 1;
        const limit = 0;
        let allOrders: Order[] = [];
        let totalPageCount = 1;

        const firstResp = await getAllOrders(page, limit, 'completed');
        if (firstResp.success) {
          const ordersData = Array.isArray(firstResp.data) ? (firstResp.data as Order[]) : [];
          totalPageCount = Number(firstResp.meta?.pages ?? 1);
          allOrders = ordersData;
        }

        for (page = 2; page <= totalPageCount; page++) {
          try {
            const resp = await getAllOrders(page, limit, 'completed');
            if (resp.success) {
              const pageOrders = Array.isArray(resp.data) ? (resp.data as Order[]) : [];
              allOrders = allOrders.concat(pageOrders);
            }
          } catch (e) {
            logger.warn('Monthly orders page fetch failed:', e);
          }
        }

        setAllOrders(allOrders);
      } catch (error) {
        logger.error('Failed to fetch monthly orders (ProfilePage):', error);
        setAllOrders([]);
      }
    };

    if (isAuthenticated) fetchMonthlyOrders();
  }, [isAuthenticated]);

  useEffect(() => {
    const monthOrders = allOrders.filter((o) => {
      const d = new Date(o.createdAt);
      return d.getFullYear() === selectedYear && d.getMonth() === selectedMonth;
    });
    setMonthlyOrders(monthOrders);
  }, [allOrders, selectedYear, selectedMonth]);

  // Compute totals, points (user) or commission (merchant), and word cloud
  useEffect(() => {
    const computeAnalytics = async () => {
      try {
        setMonthlyLoading(true);
        const orderCount = monthlyOrders.length;
        const totalAmount = monthlyOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);

        const tierName = membershipInfo?.currentTier?.name?.toLowerCase();
        const pointsRatePerThousand = tierName === 'gold' ? 15 : tierName === 'silver' ? 10 : 5;
        const pointsEarned = Math.floor(totalAmount / 1000) * pointsRatePerThousand;

        let commissionEarned = 0;
        const isMerchant = user?.activeRole === 'merchant';
        if (isMerchant) {
          monthlyOrders.forEach((o) => {
            o.items.forEach((it) => {
              const perUnit = Number(it.discountAmount) || 0;
              const qty = Number(it.quantity || 1);
              commissionEarned += perUnit * qty;
            });
          });
        }

        const itemFreq = new Map<string, { name: string; count: number }>();
        monthlyOrders.forEach((o) => {
          o.items.forEach((it) => {
            const nm = it.name;
            const rawName = typeof nm === 'object' ? nm?.my || nm?.en || '' : (nm as string) || '';
            const displayName = rawName.trim() || 'Unknown Item';
            const key = it.productId || displayName.toLowerCase();
            const qty = it.quantity || 1;
            const current = itemFreq.get(key);
            if (current) {
              itemFreq.set(key, { name: current.name, count: current.count + qty });
            } else {
              itemFreq.set(key, { name: displayName, count: qty });
            }
          });
        });
        const cloud = Array.from(itemFreq.values())
          .map(({ name, count }) => ({ word: name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 20);

        setMonthlyTotals({ orderCount, totalAmount, pointsEarned, commissionEarned });
        setWordCloud(cloud);
      } catch (error) {
        logger.error('Failed to compute monthly analytics (ProfilePage):', error);
        setMonthlyTotals({
          orderCount: 0,
          totalAmount: 0,
          pointsEarned: 0,
          commissionEarned: 0,
        });
        setWordCloud([]);
      } finally {
        setMonthlyLoading(false);
      }
    };

    if (isAuthenticated) computeAnalytics();
  }, [monthlyOrders, membershipInfo, isAuthenticated, user]);

  // Build labels for last 3 months
  const getLastThreeMonths = useCallback((): { key: string; label: string }[] => {
    const now = new Date();
    const res: { key: string; label: string }[] = [];
    for (let i = 2; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = `${d.getMonth() + 1}`.padStart(2, '0');
      const key = `${y}-${m}`;
      const label =
        i18n.language === 'mm'
          ? d.toLocaleString('my-MM', { month: 'short' })
          : d.toLocaleString(i18n.language, { month: 'short' });
      res.push({ key, label });
    }
    return res;
  }, [i18n.language]);

  // Compute revenue, orders, commission for last 3 months
  useEffect(() => {
    async function computeThreeMonths() {
      try {
        setThreeMonthLoading(true);
        const months = getLastThreeMonths();
        const groups = new Map<string, Order[]>();
        months.forEach((m) => groups.set(m.key, []));
        allOrders.forEach((o: Order) => {
          const d = new Date(o.createdAt);
          const y = d.getFullYear();
          const m = `${d.getMonth() + 1}`.padStart(2, '0');
          const key = `${y}-${m}`;
          if (groups.has(key)) groups.get(key)!.push(o);
        });

        const labels: string[] = months.map((m) => m.label);
        const revenue: number[] = months.map((m) => {
          const orders = groups.get(m.key) || [];
          return orders.reduce((sum, o: Order) => sum + (o.totalAmount || 0), 0);
        });
        const ordersArr: number[] = months.map((m) => (groups.get(m.key) || []).length);

        let commissionArr: number[] | undefined;
        const role = user?.activeRole || userProfile?.activeRole;
        if (role === 'merchant') {
          commissionArr = months.map((m) => {
            const orders = groups.get(m.key) || [];
            let sum = 0;
            orders.forEach((o: Order) => {
              o.items.forEach((it: OrderItem) => {
                const perUnit = Number(it.discountAmount) || 0;
                const qty = Number(it.quantity || 1);
                sum += perUnit * qty;
              });
            });
            return sum;
          });
        }

        setThreeMonthAgg({ labels, revenue, orders: ordersArr, commission: commissionArr });
      } catch (error) {
        logger.warn('Failed to compute three-month aggregation (ProfilePage):', error);
      } finally {
        setThreeMonthLoading(false);
      }
    }
    if (allOrders && Array.isArray(allOrders)) computeThreeMonths();
  }, [allOrders, i18n.language, user?.activeRole, userProfile?.activeRole, getLastThreeMonths]);

  // Line chart options for last 3 months
  const threeMonthLineOptions = useMemo(() => {
    const role = user?.activeRole || userProfile?.activeRole;
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 40, right: 40, top: 30, bottom: 30 },
      xAxis: { type: 'category', data: threeMonthAgg.labels },
      yAxis: [
        { type: 'value', name: i18n.language === 'mm' ? 'ငွေ (MMK)' : 'Amount (MMK)' },
        { type: 'value', name: i18n.language === 'mm' ? 'အော်ဒါ' : 'Orders', position: 'right' },
      ],
      color: ['#2563eb', '#059669', '#f59e0b'],
      series: [
        {
          name: i18n.language === 'mm' ? 'ဝင်ငွေ' : 'Revenue',
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          areaStyle: { opacity: 0.15 },
          data: threeMonthAgg.revenue,
        },
        {
          name: i18n.language === 'mm' ? 'အော်ဒါ' : 'Orders',
          type: 'line',
          smooth: true,
          yAxisIndex: 1,
          data: threeMonthAgg.orders,
        },
        ...(role === 'merchant' && threeMonthAgg.commission && threeMonthAgg.commission.length
          ? [
              {
                name: i18n.language === 'mm' ? 'ကော်မရှင်' : 'Commission',
                type: 'line',
                smooth: true,
                yAxisIndex: 0,
                data: threeMonthAgg.commission,
              },
            ]
          : []),
      ],
    };
  }, [threeMonthAgg, i18n.language, user?.activeRole, userProfile?.activeRole]);

  // Word cloud options for current month using ECharts
  const wordCloudOptions = useMemo(() => {
    if (!wordCloud || wordCloud.length === 0) return {};
    const palette = ['#2563eb', '#059669', '#f59e0b', '#ef4444', '#10b981', '#7c3aed'];
    return {
      tooltip: {},
      series: [
        {
          type: 'wordCloud',
          shape: 'circle',
          width: '100%',
          height: '100%',
          sizeRange: [12, 42],
          rotationRange: [0, 0],
          gridSize: 6,
          drawOutOfBound: false,
          textStyle: {
            color: function () {
              return palette[Math.floor(Math.random() * palette.length)];
            },
          },
          emphasis: { focus: 'self', textStyle: { shadowBlur: 8, shadowColor: '#888' } },
          data: wordCloud.map((it) => ({ name: it.word, value: it.count })),
        },
      ],
    };
  }, [wordCloud]);

  // Fetch merchant record when user is a merchant (guarded + cancellation to avoid flicker)
  useEffect(() => {
    const merchantId = user?.merchantId;
    const isMerchantRole = (user?.activeRole || userProfile?.activeRole) === 'merchant';
    const isMerchant = isAuthenticated && isMerchantRole && !!merchantId;

    let cancelled = false;

    if (!isMerchant) {
      setMerchantRecord(null);
      setMerchantRecordLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadMerchantRecord = async () => {
      try {
        setMerchantRecordLoading(true);
        const resp = await merchantService.getMerchantRecord(String(merchantId));
        if (!cancelled) {
          if (resp.success && resp.data) {
            setMerchantRecord(resp.data);
          } else {
            logger.warn('Merchant record unavailable:', resp.error || resp.message);
            setMerchantRecord(null);
          }
        }
      } catch (e) {
        if (!cancelled) {
          logger.error('Failed to load merchant record (ProfilePage):', e);
          setMerchantRecord(null);
        }
      } finally {
        if (!cancelled) {
          setMerchantRecordLoading(false);
        }
      }
    };

    loadMerchantRecord();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, userProfile]);

  const isOnline = useOnlineStatus();

  const fetchUserProfile = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const profile = await getUserInfo();
      const rawUser = profile.user;
      type RawUser =
        | {
            id?: string;
            _id?: string;
            name?: string;
            fullName?: string;
            phoneNumber?: string;
            email?: string;
            activeRole?: string;
            isVerified?: boolean;
            isEmailVerified?: boolean;
            isPhoneVerified?: boolean;
            createdAt?: string;
          }
        | null
        | undefined;
      const mappedUser: UserProfile | null =
        rawUser && typeof rawUser === 'object'
          ? {
              id: (rawUser as RawUser)?.id ?? (rawUser as RawUser)?._id ?? '',
              name: (rawUser as RawUser)?.name ?? (rawUser as RawUser)?.fullName ?? '',
              phoneNumber: (rawUser as RawUser)?.phoneNumber ?? '',
              email: (rawUser as RawUser)?.email ?? '',
              activeRole: (rawUser as RawUser)?.activeRole ?? 'user',
              isVerified: (rawUser as RawUser)?.isVerified,
              isEmailVerified: (rawUser as RawUser)?.isEmailVerified,
              isPhoneVerified: (rawUser as RawUser)?.isPhoneVerified,
              createdAt: (rawUser as RawUser)?.createdAt,
            }
          : null;

      if (mappedUser) {
        setUserProfile(mappedUser);
        setEditForm({
          name: mappedUser.name || '',
          email: mappedUser.email || '',
        });
      } else {
        setUserProfile(null);
      }
    } catch (error: unknown) {
      logger.error('Failed to fetch user profile:', error);
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('messages.failed_to_load_data');
      showToast('error', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [t, showToast, isAuthenticated]);

  // Fetch merchant status
  const fetchMerchantStatus = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      setMerchantStatusLoading(true);
      const result = await merchantService.getMerchantStatus();
      if (result.success) {
        setMerchantStatus(result.status || null);
        setMerchantStatusReason(result.reason ?? null);
        setMerchantStatusReviewedAt(result.reviewedAt ?? null);
      } else {
        setMerchantStatus(null);
        setMerchantStatusReason(null);
        setMerchantStatusReviewedAt(null);
      }
    } catch (error: unknown) {
      logger.error('Failed to fetch merchant status:', error);
      setMerchantStatus(null);
      setMerchantStatusReason(null);
      setMerchantStatusReviewedAt(null);
    } finally {
      setMerchantStatusLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUserProfile();
    fetchMerchantStatus();
  }, [fetchUserProfile, fetchMerchantStatus, isAuthenticated]);

  // Fetch point history function with pagination
  const fetchPointHistory = useCallback(
    async (page: number = 1) => {
      // Only require authentication; history API does not need userProfile.id
      if (!isAuthenticated) return;

      try {
        setPointHistoryLoading(true);

        // Use the new pagination-enabled loyalty service
        const response = await loyaltyService.getFormattedPointHistoryWithPagination(
          page,
          itemsPerPage,
        );

        setPointHistory(response.items);
        setPagination(response.pagination);
        setCurrentPage(page);
      } catch (error: unknown) {
        logger.error('Failed to fetch point history:', error);
        const errorMessage =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          t('messages.failed_to_load_data');
        showToast('error', errorMessage);
      } finally {
        setPointHistoryLoading(false);
      }
    },
    [t, showToast, isAuthenticated, itemsPerPage],
  );

  // Handle point history dialog
  const handleShowPointHistory = () => {
    setShowPointHistory(true);
    setCurrentPage(1); // Reset to first page
    fetchPointHistory(1);
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && pagination && newPage <= pagination.totalPages) {
      fetchPointHistory(newPage);
    }
  };

  // Get transaction type display
  const getTransactionTypeDisplay = (
    type: 'earned' | 'redeemed' | 'expired' | 'bonus' | 'refunded',
  ) => {
    if (type === 'earned') {
      return {
        text: i18n.language === 'mm' ? 'ရရှိ' : 'Earned',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        sign: '+',
      };
    } else if (type === 'refunded') {
      return {
        text: i18n.language === 'mm' ? 'ပြန်အမ်းခြင်း' : 'Refunded',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        sign: '^',
      };
    } else if (type === 'expired') {
      return {
        text: i18n.language === 'mm' ? 'သက်တမ်းကုန်' : 'Expired',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        sign: '-',
      };
    } else if (type === 'bonus') {
      return {
        text: i18n.language === 'mm' ? 'အပိုဆု' : 'Bonus Earned',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        sign: '+',
      };
    } else {
      return {
        text: i18n.language === 'mm' ? 'အသုံးပြု' : 'Redeemed',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        sign: '-',
      };
    }
  };

  // Get merchant status display
  const getMerchantStatusDisplay = (status: string | null) => {
    if (!status) {
      return {
        text: i18n.language === 'mm' ? 'မရှိပါ' : 'Not Applied',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        icon: LegalDocument01FreeIcons,
      };
    }

    switch (status.toLowerCase()) {
      case 'pending':
        return {
          text: i18n.language === 'mm' ? 'စောင့်ဆိုင်းဆဲ' : 'Pending Review',
          color: 'text-yellow-700',
          bgColor: 'bg-yellow-100',
          icon: Timer01FreeIcons,
        };
      case 'approved':
        return {
          text: i18n.language === 'mm' ? 'အတည်ပြုပြီး' : 'Approved',
          color: 'text-green-700',
          bgColor: 'bg-green-100',
          icon: FileVerifiedFreeIcons,
        };
      case 'rejected':
        return {
          text: i18n.language === 'mm' ? 'ငြင်းပယ်ခံရ' : 'Rejected',
          color: 'text-red-700',
          bgColor: 'bg-red-100',
          icon: CancelCircleFreeIcons,
        };
      default:
        return {
          text: status,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          icon: LegalDocument01FreeIcons,
        };
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (isEditing && userProfile) {
      setEditForm({ name: userProfile.name || '', email: userProfile.email || '' });
    }
  };

  const handleSaveProfile = async () => {
    try {
      if (userProfile) {
        setUserProfile({ ...userProfile, name: editForm.name, email: editForm.email });
        showToast('success', t('messages.profile_updated_successfully'));
      }
      setIsEditing(false);
    } catch (error: unknown) {
      logger.error('Failed to update profile:', error);
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('messages.something_went_wrong');
      showToast('error', errorMessage);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading || membershipLoading) {
    return (
      <>
        <Header />
        <div className="w-full min-h-dvh bg-[#04BFBF]/15 flex flex-col">
          <div className="px-4 py-8 flex-1 mt-[56px] pb-24 md:pb-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-6">
                <MenuSidebar
                  title={t('my_account')}
                  currentPage="profile"
                  menuItems={getMenuItems(t)}
                />
                <div className="flex-1">
                  <div className="bg-white rounded-xl overflow-hidden">
                    <div className="flex flex-col justify-center items-center h-64 space-y-4">
                      <div className="relative">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-600 border-t-transparent absolute top-0 left-0"></div>
                      </div>
                      <p className="text-gray-600 font-medium animate-pulse">
                        {t('loading_profile')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="w-full min-h-dvh bg-[#04BFBF]/15 flex flex-col">
        <div className="px-4 py-8 flex-1 mt-[56px] pb-24 md:pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              <MenuSidebar
                title={t('my_account')}
                currentPage="profile"
                menuItems={getMenuItems(t)}
              />
              <div className="flex-1">
                <div className="bg-white rounded-xl overflow-hidden">
                  <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-4 sm:p-6 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                          {t('personal_information')}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          {t('manage_your_account_information')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <ResponsiveScrollArea className="md:h-[calc(100dvh-230px)] w-full">
                    <div className="p-4 md:p-6 space-y-8">
                      {/* Personal Information & Loyalty Section */}
                      {isOnline && (
                        <div className="bg-white border border-gray-200 rounded-lg">
                          {!isAuthenticated ? (
                            <div className="p-6">
                              <div className="text-center py-8">
                                <div className="mb-4">
                                  <svg
                                    className="mx-auto h-12 w-12 text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                    />
                                  </svg>
                                </div>
                                <h4 className="text-lg font-medium text-gray-900 mb-2">
                                  {t('login_required')}
                                </h4>
                                <p className="text-gray-600 mb-4">{t('login_to_view_profile')}</p>
                                <button
                                  onClick={() => showAuthDialog('login')}
                                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                  {t('login')}
                                </button>
                              </div>
                            </div>
                          ) : isEditing ? (
                            <div className="p-4 sm:p-6">
                              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                                {t('edit_profile_details')}
                              </h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('name')}
                                  </label>
                                  <input
                                    type="text"
                                    value={editForm.name}
                                    onChange={(e) =>
                                      setEditForm({ ...editForm, name: e.target.value })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('email')}
                                  </label>
                                  <input
                                    type="email"
                                    value={editForm.email}
                                    onChange={(e) =>
                                      setEditForm({ ...editForm, email: e.target.value })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('phone_number')}
                                  </label>
                                  <p className="text-gray-500 bg-gray-100 p-3 rounded-lg text-sm">
                                    {userProfile?.phoneNumber || 'N/A'}{' '}
                                    <span className="text-xs italic">
                                      ({t('cannot_be_changed')})
                                    </span>
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('role')}
                                  </label>
                                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg capitalize">
                                    {userProfile?.activeRole || 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('member_since')}
                                  </label>
                                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
                                    {formatDate(userProfile?.createdAt)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-3 mt-6 border-t pt-6">
                                <button
                                  onClick={handleSaveProfile}
                                  className="w-full sm:w-auto px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium shadow-sm"
                                >
                                  {t('save_changes')}
                                </button>
                                <button
                                  onClick={handleEditToggle}
                                  className="w-full sm:w-auto px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                                >
                                  {t('cancel')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {userProfile?.activeRole === 'merchant' && (
                                <div className="relative w-full h-36 sm:h-48 md:h-56 rounded-t-lg overflow-hidden">
                                  <img
                                    src={coverImageUrl}
                                    alt={shopDisplayName || 'Shop cover'}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 sm:p-6">
                                <div className="flex items-center gap-4">
                                  <Avatar
                                    className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 ring-2 ring-white/30 ${getAvatarColor(userProfile?.name || 'Guest')} text-white flex items-center justify-center font-bold`}
                                    loyaltyTier={
                                      (membershipInfo?.currentTier?.name?.toLowerCase() as LoyaltyTier) ||
                                      'bronze'
                                    }
                                    showLoyaltyIcon={false}
                                    enableCoinAnimation={false}
                                    showProgressCircle={true}
                                    currentPoints={membershipInfo?.pointsBalance || 0}
                                    nextLevelPoints={membershipInfo?.currentTier?.maxPoints || 0}
                                    loyaltyDisplayMode={'corner'}
                                    lg={true}
                                    isMerchant={userProfile?.activeRole === 'merchant'}
                                  >
                                    {userProfile?.activeRole === 'merchant' ? (
                                      <HugeiconsIcon
                                        icon={Store01FreeIcons}
                                        size={36}
                                        color="white"
                                        strokeWidth={2}
                                        className="mb-2"
                                      />
                                    ) : (
                                      getUserInitials(userProfile?.name || '')
                                    )}
                                  </Avatar>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <h4 className="text-base sm:text-lg font-bold text-gray-800">
                                        {userProfile?.activeRole === 'merchant'
                                          ? shopDisplayName
                                          : userProfile?.name || 'N/A'}
                                      </h4>
                                      {(userProfile?.isEmailVerified ||
                                        userProfile?.isPhoneVerified) && (
                                        <VerifiedIcon className="w-5 h-5 text-green-500" />
                                      )}
                                      {userProfile?.activeRole === 'merchant' && (
                                        <span className="text-xs text-cyan-500 bg-cyan-100 px-2 py-1 rounded-md font-medium">
                                          Merchant
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600 break-all">
                                      {userProfile?.email || userProfile?.phoneNumber || 'N/A'}
                                    </p>
                                  </div>
                                </div>
                                <div className="w-full sm:w-auto">
                                  <button
                                    onClick={handleEditToggle}
                                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                                  >
                                    {t('edit', 'Edit')}
                                  </button>
                                </div>
                              </div>
                              {userProfile?.activeRole === 'merchant' && (
                                <div className="px-4 pb-4 sm:px-6 sm:pb-6 text-sm text-gray-700 space-y-1">
                                  {merchantRecordLoading ? (
                                    <div className="space-y-2 animate-pulse">
                                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                                      <div className="h-4 bg-gray-200 rounded w-2/5" />
                                    </div>
                                  ) : merchantRecord ? (
                                    <>
                                      {addressLine && (
                                        <p className="flex items-center gap-2">
                                          <MapPin className="w-4 h-4 text-gray-500" />
                                          <span className="line-clamp-1">{addressLine}</span>
                                        </p>
                                      )}
                                      {(merchantRecord.phoneNumber || merchantRecord.email) && (
                                        <p className="flex items-center gap-2 flex-wrap">
                                          {merchantRecord.phoneNumber && (
                                            <>
                                              <Phone className="w-4 h-4 text-gray-500" />
                                              <span>{merchantRecord.phoneNumber}</span>
                                            </>
                                          )}
                                          {merchantRecord.phoneNumber && merchantRecord.email && (
                                            <span className="mx-2 text-gray-400">•</span>
                                          )}
                                          {merchantRecord.createdAt && (
                                            <>
                                              <Calendar className="w-4 h-4 text-gray-500" />
                                              <span className="break-all">
                                                {formatDate(merchantRecord.createdAt)}
                                              </span>
                                            </>
                                          )}
                                        </p>
                                      )}
                                      {merchantRecord?.isPublic &&
                                        (merchantRecord?.id ||
                                          merchantRecord?.merchantId ||
                                          user?.merchantId) && (
                                          <div className="mt-4 flex flex-col sm:flex-row items-center gap-3">
                                            <Button
                                              variant="default"
                                              onClick={() => {
                                                const id = String(
                                                  merchantRecord?.id ||
                                                    merchantRecord?.merchantId ||
                                                    user?.merchantId ||
                                                    '',
                                                );
                                                if (!id) return;
                                                window.open(getMerchantMapLink(id), '_blank');
                                              }}
                                              className="w-full sm:w-auto"
                                            >
                                              {i18n.language === 'mm'
                                                ? 'Merchant Map ကြည့်ရန်'
                                                : 'View Merchant Map'}
                                            </Button>
                                            <Button
                                              variant="outline"
                                              onClick={async () => {
                                                const id = String(
                                                  merchantRecord?.id ||
                                                    merchantRecord?.merchantId ||
                                                    user?.merchantId ||
                                                    '',
                                                );
                                                if (!id) return;
                                                await navigator.clipboard.writeText(
                                                  getMerchantMapLink(id),
                                                );
                                                showToast(
                                                  'success',
                                                  i18n.language === 'mm'
                                                    ? 'Map link ကို ကူးယူပြီးပြီ'
                                                    : 'Map link copied',
                                                );
                                              }}
                                              className="w-full sm:w-auto"
                                            >
                                              {i18n.language === 'mm'
                                                ? 'Map Link ကူးယူရန်'
                                                : 'Copy Map Link'}
                                            </Button>
                                          </div>
                                        )}
                                    </>
                                  ) : (
                                    <p className="text-gray-500">
                                      {i18n.language === 'mm'
                                        ? 'လုပ်ငန်းအချက်အလက် မရှိသေးပါ'
                                        : 'Merchant details not available yet'}
                                    </p>
                                  )}
                                </div>
                              )}
                              {/* Loyalty Section - Only show if membership info exists or is loading */}
                              {(membershipLoading || membershipInfo) && (
                                <div className="bg-gradient-to-r from-gray-700 to-gray-900 text-white rounded-b-lg m-0 p-4 sm:p-6 space-y-4">
                                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                                    <div className="flex justify-between sm:justify-start">
                                      <div className="flex items-center justify-center w-16 h-16 rounded-full">
                                        <img
                                          src={
                                            tierIconProps.loyaltyTier === 'bronze'
                                              ? bronzeIcon
                                              : tierIconProps.loyaltyTier === 'silver'
                                                ? silverIcon
                                                : tierIconProps.loyaltyTier === 'gold'
                                                  ? goldIcon
                                                  : '/src/assets/loyalty/EZCoin.svg'
                                          }
                                          alt="Tier Icon"
                                          className="w-16 h-16 object-contain"
                                        />
                                      </div>
                                      <div className="ml-0 sm:ml-2 my-auto">
                                        <p className="text-sm text-gray-300 ">
                                          {t('loyalty.current_tier')}
                                        </p>
                                        <p
                                          className={`text-xl justify-end sm:justify-start sm:text-2xl font-bold flex items-center gap-2 uppercase`}
                                          style={{
                                            color:
                                              membershipInfo?.currentTier?.name?.toLowerCase() ===
                                              'bronze'
                                                ? '#D3A575'
                                                : membershipInfo?.currentTier?.name?.toLowerCase() ===
                                                    'silver'
                                                  ? '#C6C1AF'
                                                  : membershipInfo?.currentTier?.name?.toLowerCase() ===
                                                      'gold'
                                                    ? '#FFC200'
                                                    : membershipInfo?.currentTier?.name?.toLowerCase() ===
                                                        'platinum'
                                                      ? '#9333ea'
                                                      : '#9ca3af',
                                          }}
                                        >
                                          {membershipInfo?.currentTier?.name ||
                                            t('loyalty.not_available')}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="w-full text-left sm:w-auto sm:text-right">
                                      <p className="text-sm text-gray-300">{t('loyalty.points')}</p>
                                      <div className="text-xl sm:text-2xl font-bold overflow-hidden">
                                        <div className="flex justify-end">
                                          {pointsAnimation.digits.map((digit, index) => (
                                            <span
                                              key={`${pointsAnimation.value}-${index}`}
                                              className="inline-block"
                                              style={digit.style}
                                            >
                                              {digit.char}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="w-full bg-gray-600 rounded-full h-2.5">
                                      <div
                                        className={`${getTierProgressColor()} h-2.5 rounded-full`}
                                        style={{
                                          width: `${progressAnimation.currentPercentage}%`,
                                          willChange: 'width',
                                        }}
                                      ></div>
                                    </div>
                                    <p className="text-xs text-gray-300 mt-2 text-right">
                                      {(() => {
                                        // Handle loading state
                                        if (membershipLoading) {
                                          return t(
                                            'loyalty.loading_membership',
                                            'Loading membership...',
                                          );
                                        }

                                        // Handle null membershipInfo case
                                        if (!membershipInfo) {
                                          return t(
                                            'loyalty.no_membership',
                                            'No membership data available',
                                          );
                                        }

                                        const pointsToNext = getPointsToNextTier();

                                        // Handle case where user is at highest tier
                                        if (pointsToNext === 0) {
                                          return t(
                                            'loyalty.highest_tier_reached',
                                            'Highest tier reached!',
                                          );
                                        }

                                        return t('loyalty.next_tier_progress', {
                                          count: pointsToNext,
                                        });
                                      })()}
                                    </p>
                                  </div>

                                  {/* Tier Benefits Section */}
                                  <div className="mt-6 pt-4 border-t border-gray-600">
                                    <div className="flex justify-between items-center mb-3">
                                      <h4 className="text-lg font-semibold text-white">
                                        {i18n.language === 'mm'
                                          ? 'အဆင့်အကျိုးခံစားခွင့်များ'
                                          : 'Tier Benefits'}
                                      </h4>
                                      <Button
                                        onClick={handleShowPointHistory}
                                        variant="outline"
                                        size="sm"
                                        className="bg-transparent border-gray-400 text-gray-200 hover:bg-gray-700 hover:text-white"
                                      >
                                        <History className="w-4 h-4 mr-2" />
                                        {isDesktop
                                          ? i18n.language === 'mm'
                                            ? 'အမှတ်ရရှိမှု'
                                            : 'Point History'
                                          : i18n.language === 'mm'
                                            ? 'အမှတ်ရရှိမှု'
                                            : 'History'}
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      {getTierBenefits(
                                        membershipInfo?.currentTier?.name || 'bronze',
                                      ).map((benefit, index) => (
                                        <div key={index} className="flex items-start gap-2 text-sm">
                                          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                          <span className="text-gray-200">{benefit}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Merchant Status Section - Only show for authenticated users */}
                      {isAuthenticated && isOnline && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                          <div className="mb-4">
                            <h3 className="text-lg sm:text-2xl font-semibold text-gray-900">
                              {t('merchantStatus.title')}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {t('merchantStatus.description')}
                            </p>
                          </div>

                          {merchantStatusLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                            </div>
                          ) : (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">
                                  <HugeiconsIcon
                                    icon={getMerchantStatusDisplay(merchantStatus).icon}
                                    size="32"
                                  />
                                </span>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-700">
                                      {t('merchantStatus.status')}:
                                    </span>
                                    <span
                                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMerchantStatusDisplay(merchantStatus).bgColor} ${getMerchantStatusDisplay(merchantStatus).color}`}
                                    >
                                      {getMerchantStatusDisplay(merchantStatus).text.toUpperCase()}
                                    </span>
                                    {merchantStatusReviewedAt ? (
                                      <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMerchantStatusDisplay(merchantStatus).bgColor} ${getMerchantStatusDisplay(merchantStatus).color}`}
                                      >
                                        {/* {t('merchantStatus.reviewedAt')}:{' '} */}
                                        {formatDate(merchantStatusReviewedAt)}
                                      </span>
                                    ) : null}
                                  </div>
                                  {merchantStatus && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      {merchantStatus === 'pending' &&
                                        t('merchantStatus.pendingMessage')}
                                      {merchantStatus === 'approved' &&
                                        t('merchantStatus.approvedMessage')}
                                      {merchantStatus === 'rejected' &&
                                        `${t('merchantStatus.rejectedMessage')}` +
                                          (merchantStatusReason &&
                                          merchantStatusReason.trim().length > 0
                                            ? ` ${t('merchantStatus.reason')}: ${merchantStatusReason}`
                                            : '')}
                                      {!merchantStatus && t('merchantStatus.notAppliedMessage')}
                                      {merchantStatus === 'suspended' &&
                                        `${t('merchantStatus.suspendedMessage')}` +
                                          (merchantRecord?.deactivationReason &&
                                          merchantRecord?.deactivationReason.trim().length > 0
                                            ? ` ${t('merchantStatus.reason')}: ${merchantRecord?.deactivationReason}`
                                            : '')}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Apply as Merchant Button - Show only if not applied or rejected */}
                              {(!merchantStatus ||
                                merchantStatus === 'rejected' ||
                                merchantStatus === 'suspended') && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <button
                                    onClick={() =>
                                      (window.location.href = '/merchant-registration')
                                    }
                                    className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                  >
                                    {t('merchantStatus.applyCta')}
                                  </button>
                                  <p className="text-xs text-gray-500 mt-2">
                                    {t('merchantStatus.applyCtaDesc')}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Monthly Summary (Account Settings) */}
                      {(user?.activeRole || userProfile?.activeRole) === 'merchant' && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                          <div className="mb-4">
                            <div className="flex items-start sm:items-center justify-between gap-2">
                              <div>
                                <h3 className="text-lg sm:text-2xl font-semibold text-gray-900">
                                  {t('common.monthly_summary')}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  {t('common.monthly_summary_description')}
                                </p>
                              </div>
                              {monthlyTotals.orderCount > 0 && (
                                <button
                                  onClick={() =>
                                    openMonthlyDetails({ year: selectedYear, month: selectedMonth })
                                  }
                                  className="self-start sm:self-center px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  {i18n.language === 'mm' ? 'အသေးစိတ်' : 'View Detail'}
                                </button>
                              )}
                            </div>
                          </div>
                          {monthlyLoading ? (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                              <div className="lg:col-span-2 p-4 rounded-lg border border-gray-200 shadow-sm">
                                <div className="h-4 bg-gray-200 rounded w-40 mb-2 animate-pulse"></div>
                                <div className="h-56 bg-gray-100 rounded animate-pulse"></div>
                              </div>
                              <div className="lg:col-span-1 p-4 rounded-lg border border-gray-200 shadow-sm">
                                <div className="h-4 bg-gray-200 rounded w-40 mb-2 animate-pulse"></div>
                                <div className="h-[280px] bg-gray-100 rounded animate-pulse"></div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
                                <div className="lg:col-span-2 p-4 rounded-lg border border-gray-200 shadow-sm">
                                  <p className="text-sm font-semibold text-gray-900 mb-2">
                                    {i18n.language === 'mm'
                                      ? 'လ ၃ လ ယှဉ်၍ မြင်ပြ'
                                      : '3-Month Comparison'}
                                  </p>
                                  {threeMonthLoading ? (
                                    <div className="h-56 bg-gray-100 animate-pulse rounded" />
                                  ) : (
                                    <ReactECharts
                                      option={threeMonthLineOptions}
                                      style={{ height: 260 }}
                                    />
                                  )}
                                </div>
                                <div className="lg:col-span-1 p-4 rounded-lg border border-gray-200 shadow-sm">
                                  <p className="text-sm font-semibold text-gray-900 mb-2">
                                    {t('common.popular_this_month')}
                                  </p>
                                  {wordCloud.length > 0 ? (
                                    <div className="w-full h-[280px]">
                                      <ReactECharts
                                        option={wordCloudOptions}
                                        style={{ height: 280 }}
                                      />
                                    </div>
                                  ) : (
                                    <div className="text-sm text-gray-600">
                                      {i18n.language === 'mm'
                                        ? 'ဒီလအတွက် ဒေတာမရှိသေးပါ'
                                        : 'No data for this month'}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Monthly details moved into modal */}
                            </>
                          )}
                        </div>
                      )}

                      {/* Settings Section */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <div className="mb-6">
                          <h3 className="text-lg sm:text-2xl font-semibold text-gray-900">
                            {t('settings', 'Settings')}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {t(
                              'settings_description',
                              'Manage your preferences and application settings',
                            )}
                          </p>
                        </div>
                        <div className="space-y-6">
                          <div className="border-b border-gray-200 pb-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                              <div>
                                <h5 className="text-base font-medium text-gray-900">
                                  {t('privacy.settings.title', 'Privacy Settings')}
                                </h5>
                                <p className="text-sm text-gray-600 mt-1">
                                  {t(
                                    'privacy.settings.description',
                                    'Manage your privacy preferences and data settings',
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-gray-900">
                                    {t('privacy.settings.analytics.title', 'Analytics & Cookies')}
                                  </h5>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {t(
                                      'privacy.settings.analytics.description',
                                      'Help us improve our service by allowing analytics data collection.',
                                    )}
                                  </p>
                                </div>
                                <button
                                  onClick={() => setShowPrivacySettings(true)}
                                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                  {t('manage', 'Manage')}
                                </button>
                              </div>
                            </div>
                            <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 p-4 rounded-lg">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {t(
                                    'privacy.settings.recentPhones.label',
                                    'Save recent phone numbers',
                                  )}
                                  <span className="text-xs text-gray-500 ml-2">
                                    {saveRecentEnabled
                                      ? t(
                                          'privacy.settings.recentPhones.enabled',
                                          'Currently enabled',
                                        )
                                      : t(
                                          'privacy.settings.recentPhones.disabled',
                                          'Currently disabled',
                                        )}
                                  </span>
                                </div>
                                <div className="flex">
                                  <div className="text-xs text-gray-500"></div>
                                  <p className="text-xs text-gray-600 mt-2">
                                    {t(
                                      'privacy.settings.recentPhones.description',
                                      'ဖုန်းနံပါတ် recent list ကို သင့် device ရဲ့ local storage ထဲမှာပဲ သိမ်းထားပါတယ်။ ကျွန်တော်တို့ server တွေပေါ်မှာ မသိမ်းပါဘူး၊ ထို့ကြောင့် ပိုလုံခြုံပါတယ်။',
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center">
                                <button
                                  onClick={() => handleSaveRecentToggle(!saveRecentEnabled)}
                                  aria-label={t(
                                    'privacy.settings.recentPhones.label',
                                    'Save recent phone numbers',
                                  )}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 self-start sm:self-center ${saveRecentEnabled ? 'bg-gray-600' : 'bg-gray-200'}`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${saveRecentEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                                  />
                                </button>
                              </div>
                            </div>
                          </div>

                          {(user?.activeRole === 'merchant' ||
                            userProfile?.activeRole === 'merchant') && (
                            <div className="border-b border-gray-200 pb-6">
                              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                                <div>
                                  <h5 className="text-base font-medium text-gray-900">
                                    Offline Settings
                                  </h5>
                                  <p className="text-sm text-gray-600 mt-1">
                                    Manage offline access and local data
                                  </p>
                                </div>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                  <div className="flex-1">
                                    <h5 className="text-sm font-medium text-gray-900">
                                      Enable Offline Mode
                                    </h5>
                                    <p className="text-xs text-gray-600 mt-1">
                                      Allow access to orders and profile when no internet connection
                                      is available.
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleOfflineModeToggle(!offlineModeEnabled)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 self-start sm:self-center ${offlineModeEnabled ? 'bg-gray-600' : 'bg-gray-200'}`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${offlineModeEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                                    />
                                  </button>
                                </div>

                                {isSyncing && (
                                  <div className="space-y-2 mt-4">
                                    <div className="flex justify-between text-xs text-gray-600">
                                      <span>Downloading offline data...</span>
                                      <span>{syncProgress}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${syncProgress}%` }}
                                      />
                                    </div>
                                  </div>
                                )}

                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-200">
                                  <div className="flex-1">
                                    <h5 className="text-sm font-medium text-gray-900">
                                      Clear Local Data
                                    </h5>
                                    <p className="text-xs text-gray-600 mt-1">
                                      Remove all locally saved orders and profile data.
                                    </p>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleClearLocalData}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                  >
                                    Remove all order from local
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="border-b border-gray-200 pb-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                              <div>
                                <h5 className="text-base font-medium text-gray-900">
                                  {t('language.settings.title', 'Language Settings')}
                                </h5>
                                <p className="text-sm text-gray-600 mt-1">
                                  {t(
                                    'language.settings.description',
                                    'Choose your preferred language for the application',
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div className="mb-2 sm:mb-0">
                                  <h5 className="text-sm font-medium text-gray-900">
                                    {t('language.settings.default_language', 'Default Language')}
                                  </h5>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {t(
                                      'language.settings.current_language',
                                      `Currently set to: ${i18n.language === 'en' ? 'English' : 'မြန်မာ'}`,
                                    )}
                                  </p>
                                </div>
                                <div className="flex gap-2 self-start sm:self-center">
                                  <button
                                    onClick={() => {
                                      changeLanguage('en');
                                      showToast(
                                        'success',
                                        t(
                                          'language.changed_to_english',
                                          'Language changed to English',
                                        ),
                                      );
                                    }}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${i18n.language === 'en' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
                                  >
                                    English
                                  </button>
                                  <button
                                    onClick={() => {
                                      changeLanguage('mm');
                                      showToast(
                                        'success',
                                        t(
                                          'language.changed_to_myanmar',
                                          'ဘာသာစကားကို မြန်မာသို့ ပြောင်းလဲပြီးပါပြီ',
                                        ),
                                      );
                                    }}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${i18n.language === 'mm' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
                                  >
                                    မြန်မာ
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                              <div>
                                <h5 className="text-base font-medium text-gray-900">
                                  {t('bot.settings.title', 'Bot Settings')}
                                </h5>
                                <p className="text-sm text-gray-600 mt-1">
                                  {t(
                                    'bot.settings.description',
                                    'Configure chatbot and automated assistance features',
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-gray-900">
                                    {t('bot.settings.chatbot.title', 'Support Live Chatbot')}
                                    {/* <span className="ml-2 inline-flex items-center rounded-md bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-700">
                                      <img src={AI} alt="AI" className="w-4 h-4" />
                                    </span> */}
                                  </h5>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {t(
                                      'bot.settings.chatbot.description',
                                      'Enable or disable the AI support chatbot for instant assistance.',
                                    )}
                                  </p>
                                </div>
                                <button
                                  onClick={() => {
                                    const newValue = !chatbotEnabled;
                                    setChatbotEnabled(newValue);
                                    localStorage.setItem(
                                      'chatbotEnabled',
                                      JSON.stringify(newValue),
                                    );
                                    showToast(
                                      'success',
                                      newValue
                                        ? t(
                                            'bot.settings.chatbot.enabled',
                                            'Chatbot enabled successfully',
                                          )
                                        : t(
                                            'bot.settings.chatbot.disabled',
                                            'Chatbot disabled successfully',
                                          ),
                                    );
                                    setTimeout(() => {
                                      window.location.reload();
                                    }, 1000);
                                  }}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 self-start sm:self-center ${chatbotEnabled ? 'bg-gray-600' : 'bg-gray-200'}`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${chatbotEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="border-t border-gray-200 pt-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                              <div>
                                <h5 className="text-base font-medium text-gray-900">
                                  {t('notification.settings.title', 'Notification Settings')}
                                </h5>
                                <p className="text-sm text-gray-600 mt-1">
                                  {t(
                                    'notification.settings.description',
                                    'Configure push notifications and alerts for payments and updates',
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-gray-900">
                                    {t(
                                      'notification.settings.browser.title',
                                      'Browser Notifications',
                                    )}
                                  </h5>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {t(
                                      'notification.settings.browser.description',
                                      'Get notified about payment status and important updates',
                                    )}
                                  </p>
                                  {!isSupported && (
                                    <p className="text-xs text-red-600 mt-1">
                                      {t(
                                        'notification.not_supported',
                                        'Notifications not supported in this browser',
                                      )}
                                    </p>
                                  )}
                                  {permission === 'denied' && (
                                    <p className="text-xs text-orange-600 mt-1">
                                      {t(
                                        'notification.permission_denied',
                                        'Notification permission denied. Please enable in browser settings.',
                                      )}
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={async () => {
                                    if (!isSupported) {
                                      showToast(
                                        'error',
                                        t(
                                          'notification.not_supported',
                                          'Notifications not supported',
                                        ),
                                      );
                                      return;
                                    }
                                    const newValue = !notificationsEnabled;
                                    if (newValue && permission !== 'granted') {
                                      try {
                                        const result = await requestPermission();
                                        if (result !== 'granted') {
                                          showToast(
                                            'error',
                                            t(
                                              'notification.permission_required',
                                              'Notification permission required',
                                            ),
                                          );
                                          return;
                                        }
                                      } catch (error) {
                                        logger.error(
                                          'Error requesting notification permission:',
                                          error,
                                        );
                                        showToast(
                                          'error',
                                          t(
                                            'notification.permission_error',
                                            'Error requesting notification permission',
                                          ),
                                        );
                                        return;
                                      }
                                    }
                                    setNotificationsEnabled(newValue);
                                    localStorage.setItem(
                                      'notificationsEnabled',
                                      JSON.stringify(newValue),
                                    );
                                    showToast(
                                      'success',
                                      newValue
                                        ? t(
                                            'notification.enabled',
                                            'Notifications enabled successfully',
                                          )
                                        : t(
                                            'notification.disabled',
                                            'Notifications disabled successfully',
                                          ),
                                    );
                                    if (newValue && permission === 'granted') {
                                      setTimeout(async () => {
                                        try {
                                          const notification = await showNotification({
                                            title: 'Notification Enabled',
                                            body: t(
                                              'notification.enabled_message',
                                              'Notifications are now enabled!',
                                            ),
                                            icon: '/icon-192x192.svg',
                                            tag: 'notification-enabled',
                                          });
                                          if (notification) {
                                            setTimeout(() => {
                                              notification.close();
                                            }, 4000);
                                          }
                                        } catch (error) {
                                          logger.error('Error sending notification:', error);
                                        }
                                      }, 500);
                                    }
                                  }}
                                  disabled={!isSupported}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 self-start sm:self-center ${notificationsEnabled && permission === 'granted' ? 'bg-gray-600' : 'bg-gray-200'} ${!isSupported ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationsEnabled && permission === 'granted' ? 'translate-x-6' : 'translate-x-1'}`}
                                  />
                                </button>
                              </div>
                              <div className="text-xs text-gray-500 bg-white p-3 rounded border">
                                <div className="flex justify-between items-center">
                                  <span>
                                    {t('notification.permission_status', 'Permission Status')}:
                                  </span>
                                  <span
                                    className={`font-medium ${permission === 'granted' ? 'text-green-600' : permission === 'denied' ? 'text-red-600' : 'text-orange-600'}`}
                                  >
                                    {t(permission || 'default').toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                  <div className="flex-1 text-sm font-medium text-gray-900">
                                    {t('notification.sound', 'Notification Sound')}
                                  </div>
                                  <button
                                    onClick={() => {
                                      const newValue = !notificationSoundEnabled;
                                      setNotificationSoundEnabled(newValue);
                                      localStorage.setItem(
                                        'notificationSoundEnabled',
                                        JSON.stringify(newValue),
                                      );
                                      showToast(
                                        'success',
                                        newValue
                                          ? t(
                                              'notification.sound_enabled',
                                              'Notification sound enabled',
                                            )
                                          : t(
                                              'notification.sound_disabled',
                                              'Notification sound disabled',
                                            ),
                                      );
                                    }}
                                    disabled={!notificationsEnabled || permission !== 'granted'}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 self-start sm:self-center ${!notificationsEnabled || permission !== 'granted' ? 'opacity-50 cursor-not-allowed' : ''} ${notificationSoundEnabled && notificationsEnabled && permission === 'granted' ? 'bg-gray-600' : 'bg-gray-200'}`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSoundEnabled && notificationsEnabled && permission === 'granted' ? 'translate-x-6' : 'translate-x-1'}`}
                                    />
                                  </button>
                                </div>
                                <div className="mt-2 text-xs text-gray-500">
                                  <span>{t('status', 'Status')}:</span>
                                  <span className="ml-1 font-medium">
                                    {notificationSoundEnabled &&
                                    notificationsEnabled &&
                                    permission === 'granted'
                                      ? t('enabled', 'Enabled')
                                      : t('disabled', 'Disabled')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="border-t border-gray-200 pt-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="sm:flex items-center justify-between gap-4">
                                <div>
                                  <h5 className="text-sm font-medium text-gray-900">
                                    {t('settings.version', 'Version')}
                                  </h5>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {getFullVersionInfo()}
                                  </p>
                                </div>
                                <div className="mt-2 sm:mt-0 w-full sm:w-auto flex items-center">
                                  {isPWAInstalled ? (
                                    <span className="px-3 py-1 text-xs rounded-full bg-green-100 text-green-700">
                                      {t('webapp.installed', 'Installed')}
                                    </span>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="w-full sm:w-auto"
                                      onClick={() => navigate('/webapp/install')}
                                    >
                                      {t('webapp.install', 'WebApp Install')}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ResponsiveScrollArea>
                </div>
              </div>
            </div>
          </div>
        </div>
        <PrivacySettings open={showPrivacySettings} onOpenChange={setShowPrivacySettings} />

        {/* Point History Modal */}
        {typeof document !== 'undefined' && showPointHistory
          ? createPortal(
              <Modal
                isOpen={showPointHistory}
                onClose={() => setShowPointHistory(false)}
                size="sm"
                className="m-4"
              >
                <ModalHeader>
                  <div className="flex items-center justify-between">
                    <ModalTitle className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <History className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {i18n.language === 'mm' ? 'အမှတ်ရရှိမှု' : 'Point History'}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          {i18n.language === 'mm'
                            ? 'သင့်ရဲ့ အမှတ်ရရှိမှုများကို ကြည့်ရှုပါ'
                            : 'View your point earning history'}
                        </p>
                      </div>
                    </ModalTitle>
                    <ModalCloseButton onClose={() => setShowPointHistory(false)} />
                  </div>
                </ModalHeader>

                <ModalContent>
                  {/* Point Summary Section removed as requested */}

                  <ScrollArea className="h-[60vh] p-6">
                    {pointHistoryLoading ? (
                      <div className="space-y-4">
                        {[...Array(3)].map((_, index) => (
                          <div key={index} className="animate-pulse">
                            <div className="border border-gray-200 rounded-xl p-6">
                              <div className="flex justify-between items-start">
                                <div className="space-y-2">
                                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                                </div>
                                <div className="space-y-2 text-right">
                                  <div className="h-5 bg-gray-200 rounded w-20"></div>
                                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                                </div>
                              </div>
                              <div className="h-3 bg-gray-200 rounded w-40"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : pointHistory.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <History className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {i18n.language === 'mm'
                            ? 'အမှတ်ရရှိမှု မရှိပါ'
                            : 'No point history found'}
                        </h3>
                        <p className="text-gray-500">
                          {i18n.language === 'mm'
                            ? 'အော်ဒါများ မှာယူပြီး အမှတ်များရယူပါ'
                            : 'Start earning points by placing orders'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {pointHistory.map((item, index) => {
                          const transactionDisplay = getTransactionTypeDisplay(item.type);
                          return (
                            <div
                              key={item._id}
                              className="border border-gray-200 rounded-xl shadow-md transition-all duration-200 hover:border-blue-200 bg-white"
                              style={{
                                animation: `slideInUp 0.3s ease-out ${index * 0.1}s both`,
                              }}
                            >
                              <div className="sm:flex justify-between items-start">
                                <div className="flex-1 p-6">
                                  <div className="flex items-center gap-3 mb-2">
                                    <div
                                      className={`px-2 py-1 rounded-full text-xs font-medium ${transactionDisplay.bgColor} ${transactionDisplay.color}`}
                                    >
                                      {transactionDisplay.text}
                                    </div>
                                  </div>

                                  <h4 className="font-semibold text-gray-900 text-lg mb-2">
                                    {item.description}
                                  </h4>

                                  <div className="space-y-1 text-sm text-gray-600">
                                    <p>
                                      {i18n.language === 'mm' ? 'အော်ဒါနံပါတ်' : 'Order Number'}:
                                      <span className="font-medium ml-1">{item.orderNumber}</span>
                                    </p>
                                    {/* <p>
                                        {i18n.language === 'mm' ? 'စုစုပေါင်းပမာဏ' : 'Total Amount'}:
                                        <span className="font-medium ml-1">
                                          {item.totalAmount.toLocaleString()} {i18n.language === 'mm' ? 'ကျပ်' : 'MMK'}
                                        </span>
                                      </p> */}
                                  </div>

                                  <p className="text-sm text-gray-600 mt-3">
                                    <Calendar className="w-4 h-4 inline mr-2" />
                                    {new Date(item.createdAt).toLocaleString(
                                      i18n.language === 'mm' ? 'my-MM' : 'en-US',
                                      {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true,
                                      },
                                    )}
                                  </p>
                                </div>
                                <div className="text-center sm:text-right">
                                  <div className="relative overflow-hidden">
                                    {/* Mobile: Zigzag pointing downward */}
                                    <div
                                      className={`md:hidden ${item.type === 'earned' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-red-500 to-red-600'} text-white px-4 py-2 rounded-b-lg relative`}
                                    >
                                      <div className="text-2xl font-bold">
                                        {transactionDisplay.sign}
                                        {item.points}
                                      </div>
                                      <div className="text-xs opacity-90">
                                        {i18n.language === 'mm' ? 'မှတ်' : 'Points'}
                                      </div>
                                    </div>
                                    {/* Desktop: Zigzag pointing upward */}
                                    <div
                                      className={`hidden md:block ${item.type === 'earned' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-red-500 to-red-600'} text-white px-4 py-2 rounded-tr-lg rounded-bl-lg relative`}
                                    >
                                      <div className="text-2xl font-bold">
                                        {transactionDisplay.sign}
                                        {item.points}
                                      </div>
                                      <div className="text-xs opacity-90">
                                        {i18n.language === 'mm' ? 'မှတ်' : 'Points'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {/* Pagination Controls */}
                        {pagination && pagination.totalPages > 1 && (
                          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                            <div className="text-sm text-gray-600">
                              {i18n.language === 'mm'
                                ? `စုစုပေါင်း ${pagination.total} ခု၊ စာမျက်နှာ ${pagination.page} / ${pagination.totalPages}`
                                : `Total ${pagination.total} items, Page ${pagination.page} of ${pagination.totalPages}`}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={!pagination.hasPrev || pointHistoryLoading}
                                className="flex items-center gap-1"
                              >
                                <ChevronLeft className="w-4 h-4" />
                                {i18n.language === 'mm' ? 'ရှေ့' : 'Previous'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={!pagination.hasNext || pointHistoryLoading}
                                className="flex items-center gap-1"
                              >
                                {i18n.language === 'mm' ? 'နောက်' : 'Next'}
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <ScrollBar orientation="vertical" />
                  </ScrollArea>
                </ModalContent>
              </Modal>,
              document.body,
            )
          : null}
      </div>
    </>
  );
}
